import {
	Dialog,
	DialogContent,
	DialogProps,
	DialogTitle,
	TextField,
	Box,
	DialogActions,
	Button,
	CircularProgress,
	Typography,
} from "@material-ui/core";
import { FC, useEffect } from "react";
import {
	LegacyProject,
	isChecklistRevision,
} from "../../models/Proyecto";
import { ResponsibleAutocomplete } from "../ResponsibleAutocomplete";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store/reducer";
import { IUsersState } from "src/redux/reducers/usersReducer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { updateProject } from "../../redux/actions/projectsActions";
import { getUsers } from "src/redux/actions/usersActions";

type Props = DialogProps & { project: LegacyProject };

export const EditRevisionDialog: FC<Props> = ({
	project,
	...props
}) => {
	const dispatch = useDispatch();
	const { users } = useSelector<RootState, IUsersState>(
		(s) => s.usersReducer
	);

	const {
		values,
		setFieldValue: setValue,
		errors,
		handleChange,
		handleSubmit,
		isSubmitting,
		dirty,
		resetForm,
	} = useFormik({
		enableReinitialize: true,
		validateOnChange: false,
		onSubmit: async ({ Responsable, ...values }) => {
			console.log('🔄 [EditRevisionDialog] Submitting with values:', {
				originalProject: project,
				formValues: values,
				mergedData: { ...project, ...values },
				Responsable
			});
			
			// Ensure numeric fields are actually numbers
			const processedValues = {
				...values,
				DiasPorVencer: typeof values.DiasPorVencer === 'string' 
					? parseInt(values.DiasPorVencer, 10) 
					: values.DiasPorVencer,
				DiasVencidos: typeof values.DiasVencidos === 'string' 
					? parseInt(values.DiasVencidos, 10) 
					: values.DiasVencidos
			};
			
			console.log('🔄 [EditRevisionDialog] Processed values:', processedValues);
			
			try {
				await dispatch(
					updateProject(
						{ ...project, ...processedValues },
						Responsable || undefined
					)
				);
				console.log('✅ [EditRevisionDialog] Update successful');
				props.onClose?.({}, "escapeKeyDown");
			} catch (error) {
				console.error('❌ [EditRevisionDialog] Update failed:', error);
			}
		},
		initialValues: {
			NombreProyecto: project.NombreProyecto || '',
			Responsable:
				users?.find(
					(u) => u.id === project.Responsable.FirebaseId
				) || null,
			DiasPorVencer: project.DiasPorVencer ?? 10,
			DiasVencidos: project.DiasVencidos ?? 20,
		},
		validationSchema: Yup.object({
			NombreProyecto: Yup.string().required("Requerido"),
			DiasPorVencer: Yup.number().min(
				0,
				"Debe ser mayor a 0"
			),
			DiasVencidos: Yup.number().min(
				0,
				"Debe ser mayor a 0"
			),
		}),
	});

	useEffect(() => {
		if (!props.open) return;
		console.log('🔍 [EditRevisionDialog] Project data RECIBIDO:', {
			id: project.id,
			NombreProyecto: project.NombreProyecto,
			DiasPorVencer: project.DiasPorVencer,
			DiasVencidos: project.DiasVencidos,
			DiasPorVencer_type: typeof project.DiasPorVencer,
			DiasVencidos_type: typeof project.DiasVencidos,
			fullProject: project
		});
		console.log('🔍 [EditRevisionDialog] Form INITIAL values:', {
			DiasPorVencer: values.DiasPorVencer,
			DiasVencidos: values.DiasVencidos
		});
		console.log('🔍 [EditRevisionDialog] Valores que deberían aparecer en los inputs:');
		console.log(`   DiasPorVencer: ${project.DiasPorVencer} (${typeof project.DiasPorVencer})`);
		console.log(`   DiasVencidos: ${project.DiasVencidos} (${typeof project.DiasVencidos})`);
		
		// Debug de los initialValues que se están configurando
		const initialValues = {
			NombreProyecto: project.NombreProyecto,
			Responsable: users?.find((u) => u.id === project.Responsable.FirebaseId) || null,
			DiasPorVencer: project.DiasPorVencer,
			DiasVencidos: project.DiasVencidos,
		};
		console.log('🔍 [EditRevisionDialog] InitialValues configurados:', initialValues);
		
		dispatch(getUsers());
	}, [props.open]);

	// Monitor changes in form values
	useEffect(() => {
		if (props.open) {
			console.log('🔄 [EditRevisionDialog] Form values changed:', {
				DiasPorVencer: values.DiasPorVencer,
				DiasVencidos: values.DiasVencidos,
				allValues: values
			});
		}
	}, [values.DiasPorVencer, values.DiasVencidos, props.open]);

	// Force form reset when project changes
	useEffect(() => {
		if (props.open && project.id) {
			console.log('🔄 [EditRevisionDialog] Forcing form reset with project data:', {
				projectId: project.id,
				DiasPorVencer: project.DiasPorVencer,
				DiasVencidos: project.DiasVencidos
			});
			
			// Method 1: Reset entire form
			resetForm({
				values: {
					NombreProyecto: project.NombreProyecto || '',
					Responsable: users?.find((u) => u.id === project.Responsable.FirebaseId) || null,
					DiasPorVencer: project.DiasPorVencer ?? 10,
					DiasVencidos: project.DiasVencidos ?? 20,
				}
			});
			
			// Method 2: Force individual field updates with setTimeout to ensure they apply
			setTimeout(() => {
				console.log('🔄 [EditRevisionDialog] Setting individual field values:');
				setValue('DiasPorVencer', project.DiasPorVencer ?? 10);
				setValue('DiasVencidos', project.DiasVencidos ?? 20);
				console.log('🔄 [EditRevisionDialog] Values after setValue:', {
					DiasPorVencer: values.DiasPorVencer,
					DiasVencidos: values.DiasVencidos
				});
			}, 100);
		}
	}, [project.id, project.DiasPorVencer, project.DiasVencidos, props.open, users]);

	return (
		<Dialog
			fullWidth
			{...props}
			key={`edit-dialog-${project.id}-${project.DiasPorVencer}-${project.DiasVencidos}`}
			PaperProps={{
				component: "form",
				onSubmit: handleSubmit as any,
			}}
		>
			<DialogTitle>
				<Typography variant="h4">
					{project.NombreProyecto}
				</Typography>
				<Typography variant="body1">
					Editar propiedades
				</Typography>
			</DialogTitle>

			<DialogContent
				style={{
					display: "flex",
					gap: 20,
					padding: 20,
					flexDirection: "column",
				}}
			>
				<TextField
					fullWidth
					label="Nombre del proyecto"
					value={values.NombreProyecto}
					id="NombreProyecto"
					onChange={handleChange}
					error={!!errors.NombreProyecto}
					helperText={errors.NombreProyecto}
				/>

				<ResponsibleAutocomplete
					label="Responsable"
					value={values.Responsable}
					onChange={(user) => {
						setValue("Responsable", user);
					}}
					error={!!errors.Responsable}
					helperText={errors.Responsable}
				/>

				{!isChecklistRevision(project) && (
					<Box
						display="flex"
						flexDirection="row"
						style={{ gap: 20 }}
					>
						<TextField
							type="number"
							label="Días por vencer"
							value={values.DiasPorVencer ?? project.DiasPorVencer ?? 10}
							id="DiasPorVencer"
							onChange={handleChange}
							error={!!errors.DiasPorVencer}
							helperText={errors.DiasPorVencer}
						/>
						<TextField
							type="number"
							label="Días vencidos"
							value={values.DiasVencidos ?? project.DiasVencidos ?? 20}
							id="DiasVencidos"
							onChange={handleChange}
							error={!!errors.DiasVencidos}
							helperText={errors.DiasVencidos}
						/>
					</Box>
				)}
			</DialogContent>

			<DialogActions>
				<Button
					disabled={isSubmitting}
					onClick={(ev) =>
						props.onClose?.(ev, "escapeKeyDown")
					}
				>
					Cancelar
				</Button>

				<Button
					disabled={isSubmitting}
					type="submit"
					variant="contained"
					color="primary"
				>
					{isSubmitting ? (
						<CircularProgress size={24} />
					) : (
						"Guardar"
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
